'use client';

import { type LandingTheme, type ContentBoxStyle } from '@fitvent/types';
import { V2BackgroundEffect, type BackgroundEffectType } from '@/components/public/v2/V2BackgroundEffect';
import { fontFormatHint } from '@/lib/utils/font-format';
import { safeCssColors, safeCssFontFamily, safeCssFontName } from '@/lib/utils/safe-css';

interface ThemedEventPageProps {
  children: React.ReactNode;
  theme: LandingTheme;
  organizerName?: string;
  organizerLogo?: string | null;
  backgroundEffect?: BackgroundEffectType;
  hasMobileTicketBar?: boolean;
  /** When false, hides the "Powered by Fitvent" header link and footer. Used
   * by /demo pages embedded in the landing site (the brand showcase wants
   * the page to read as the organiser's, not as a Fitvent demo). */
  showPoweredBy?: boolean;
  headerBranding?: {
    showLogo?: boolean;
    showName?: boolean;
    customName?: string;
    customLogoUrl?: string;
  };
  uploadedFonts?: {
    /**
     * sizeAdjust scales the glyphs inside the em box, as a percentage. Display
     * faces often have a small x-height, so they read noticeably smaller than a
     * text face at the same px size (Crackhouse at 20px next to Cabin at 20px).
     * Adjusting this rather than the font-size keeps every layout, line height
     * and breakpoint untouched and only changes how large the letters draw.
     * Unset means 100%, i.e. exactly today's behaviour.
     */
    heading?: { name: string; url: string; sizeAdjust?: number };
    body?: { name: string; url: string; sizeAdjust?: number };
    brand?: { name: string; url: string; sizeAdjust?: number };
  };
}

export function ThemedEventPage({ children, theme, organizerName, organizerLogo, backgroundEffect = 'none', hasMobileTicketBar = false, showPoweredBy = true, headerBranding, uploadedFonts }: ThemedEventPageProps) {
  const { fontStyle, colors: rawColors, customFonts, borderRadius = 'rounded', headerWeight = 'bold', brandNameFont: rawBrandNameFont, contentBoxStyle = 'white' } = theme;

  /**
   * These values are interpolated into the <style> block below, and they come
   * from events.metadata.landingThemeCustomColors, which the landing-theme
   * route writes straight from the request body. Unsanitised, a colour of
   * `red; } body::before { ... }` closes the declaration and appends arbitrary
   * rules to this public page.
   *
   * Sanitised once, here, so all 61 interpolations downstream inherit the
   * guarantee and a new one cannot forget it.
   */
  const colors = safeCssColors(rawColors, '#000000');
  const brandNameFont = safeCssFontFamily(rawBrandNameFont, '');

  // Default to showing both if not specified
  const showLogo = headerBranding?.showLogo ?? true;
  const showName = headerBranding?.showName ?? true;

  // Use custom values if provided, otherwise fall back to organizer profile
  const displayName = headerBranding?.customName || organizerName || '';
  const displayLogo = headerBranding?.customLogoUrl || organizerLogo || '';

  // Determine border radius value
  const borderRadiusValue = borderRadius === 'rounded' ? '0.5rem' : '0';

  // Helper to check if a color is dark
  const isDarkColor = (hexColor: string): boolean => {
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };

  // Ensure Fitvent branding always stands out from background
  const getFitventBrandingColor = (): string => {
    const primaryBrightness = isDarkColor(colors.primary);
    if (primaryBrightness) {
      return '#FFFFFF';
    } else {
      return '#111827';
    }
  };

  const fitventBrandingColor = getFitventBrandingColor();

  // Content box style configuration
  const getCardStyles = (): { bg: string; text: string; heading: string; muted: string; border: string } => {
    switch (contentBoxStyle) {
      case 'black':
        return {
          bg: '#000000',
          text: '#FFFFFF',
          heading: '#FFFFFF',
          muted: '#D1D5DB',
          border: '#374151',
        };
      case 'transparent':
        return {
          bg: 'transparent',
          text: colors.text,
          heading: colors.text,
          muted: colors.textMuted,
          border: colors.text + '33', // 20% opacity
        };
      case 'white':
      default:
        return {
          bg: '#FFFFFF',
          text: '#111827',
          heading: '#111827',
          muted: '#6B7280',
          border: '#E5E7EB',
        };
    }
  };

  const cardStyles = getCardStyles();

  // Compute numeric heading weight for CSS variable
  const getNumericHeaderWeight = (): string => {
    switch (headerWeight) {
      case 'normal': return '400';
      case 'medium': return '500';
      case 'semibold': return '600';
      case 'extrabold': return '800';
      case 'bold': default: return '700';
    }
  };

  // Compute secondary background for --fv-bg-secondary
  const getBgSecondary = (): string => {
    switch (contentBoxStyle) {
      case 'black': return '#000000';
      case 'transparent': return colors.text + '14';
      case 'white': default: return '#f3f4f6';
    }
  };

  // Determine font classes based on style
  const getFontClass = () => {
    if (fontStyle === 'custom' && customFonts?.body) {
      return '';
    }
    switch (fontStyle) {
      case 'modern':
        return 'font-[family-name:var(--font-dm-sans)]';
      case 'minimal':
        return 'font-[family-name:var(--font-inter)]';
      case 'bold':
        return 'font-[family-name:var(--font-geist-sans)]';
      case 'classic':
        return '';
      default:
        return '';
    }
  };

  const getHeadingFontClass = () => {
    // Only use uploaded/custom fonts when fontStyle is 'custom'
    if (fontStyle === 'custom') {
      if (uploadedFonts?.heading) {
        return `'${safeCssFontName(uploadedFonts.heading.name, 'sans-serif')}', sans-serif`;
      }
      if (customFonts?.heading) {
        return `'${safeCssFontName(customFonts.heading, 'sans-serif')}', sans-serif`;
      }
    }
    switch (fontStyle) {
      case 'modern':
        return 'var(--font-caprasimo)';
      case 'bold':
        return 'var(--font-bebas-neue)';
      case 'minimal':
        return 'var(--font-inter)';
      case 'classic':
        return 'Arial, Helvetica, sans-serif';
      default:
        return '';
    }
  };

  const getHeadingWeight = () => {
    // Use custom header weight if specified
    if (headerWeight) {
      switch (headerWeight) {
        case 'normal':
          return 'font-weight: 400;';
        case 'medium':
          return 'font-weight: 500;';
        case 'semibold':
          return 'font-weight: 600;';
        case 'bold':
          return 'font-weight: 700;';
        case 'extrabold':
          return 'font-weight: 800;';
        default:
          return 'font-weight: 700;';
      }
    }

    // Fallback to font style defaults
    if (fontStyle === 'custom') {
      return 'font-weight: 700;';
    }
    // Add explicit bold for classic and minimal
    switch (fontStyle) {
      case 'classic':
      case 'minimal':
        return 'font-weight: 700;';
      case 'bold':
        return 'font-weight: 600;';
      default:
        return '';
    }
  };

  const getBodyFontFamily = () => {
    // Only use uploaded/custom fonts when fontStyle is 'custom'
    if (fontStyle === 'custom') {
      if (uploadedFonts?.body) {
        return `'${safeCssFontName(uploadedFonts.body.name, 'sans-serif')}', sans-serif`;
      }
      if (customFonts?.body) {
        return `'${safeCssFontName(customFonts.body, 'sans-serif')}', sans-serif`;
      }
    }
    return '';
  };

  return (
    <>
      {/* Load custom Google Fonts if specified (only if not using uploaded fonts) */}
      {fontStyle === 'custom' && customFonts?.heading && !uploadedFonts?.heading && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${customFonts.heading.replace(/ /g, '+')}:wght@400;700&display=swap`}
          rel="stylesheet"
        />
      )}
      {fontStyle === 'custom' && customFonts?.body && !uploadedFonts?.body && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${customFonts.body.replace(/ /g, '+')}:wght@400;700&display=swap`}
          rel="stylesheet"
        />
      )}
      {brandNameFont && !uploadedFonts?.brand && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${brandNameFont.replace(/ /g, '+')}:wght@400;700&display=swap`}
          rel="stylesheet"
        />
      )}

      {/* Load uploaded custom fonts via @font-face.
          Two iOS Safari gotchas, both fixed here:
          1. format() hint - without it iOS can refuse to load the file and
             silently fall back to a system font (a TTF/woff2 that rendered on
             desktop didn't pull through on iPhone). Derived from the extension.
          2. font-weight RANGE not `normal`. Uploaded fonts are a single
             master, but headings render at font-weight: 700. iOS WebKit
             would not match a 700 request against a point-declared 400 face
             and fell back to a system font; a range keeps the face matching
             at every requested weight.
          The range deliberately stops at 500, NOT 900: a face that claims to
          cover 700 is used as-is with no synthetic bold, so a Light/Regular
          master rendered every bold element thin (the washed-out Get Tickets
          bar on fonts like Brutal Type Light). Capping the declared range at
          500 keeps the face matched (iOS safe) while letting the browser
          synthesise the missing bold weights. */}
      {((fontStyle === 'custom' && (uploadedFonts?.heading || uploadedFonts?.body)) || uploadedFonts?.brand) && (
        <style>{`
          ${fontStyle === 'custom' && uploadedFonts?.heading ? `
            @font-face {
              font-family: '${uploadedFonts.heading.name}';
              src: url('${uploadedFonts.heading.url}')${fontFormatHint(uploadedFonts.heading.url)};
              font-weight: 100 500;
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.heading.sizeAdjust ? `size-adjust: ${uploadedFonts.heading.sizeAdjust}%;` : ''}
            }
          ` : ''}
          ${fontStyle === 'custom' && uploadedFonts?.body ? `
            @font-face {
              font-family: '${uploadedFonts.body.name}';
              src: url('${uploadedFonts.body.url}')${fontFormatHint(uploadedFonts.body.url)};
              font-weight: 100 500;
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.body.sizeAdjust ? `size-adjust: ${uploadedFonts.body.sizeAdjust}%;` : ''}
            }
          ` : ''}
          ${uploadedFonts?.brand ? `
            @font-face {
              font-family: '${uploadedFonts.brand.name}';
              src: url('${uploadedFonts.brand.url}')${fontFormatHint(uploadedFonts.brand.url)};
              font-weight: 100 500;
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.brand.sizeAdjust ? `size-adjust: ${uploadedFonts.brand.sizeAdjust}%;` : ''}
            }
          ` : ''}
        `}</style>
      )}

      <div
        suppressHydrationWarning
        className={`min-h-screen ${getFontClass()}`}
        style={{
          position: 'relative',
          color: colors.text,
          ['--theme-secondary' as any]: colors.secondary,
          ['--button-text' as any]: colors.buttonText || 'white',
          ['--border-radius' as any]: borderRadiusValue,
          ...(fontStyle === 'custom' && customFonts?.body && { fontFamily: getBodyFontFamily() }),
          ...(fontStyle === 'classic' && { fontFamily: 'Arial, Helvetica, sans-serif' }),
        }}
      >
      {/* Background effect - absolute positioned inside this relative wrapper */}
      <V2BackgroundEffect effect={backgroundEffect} />

      <style dangerouslySetInnerHTML={{ __html: `
        body { background-color: ${colors.primary}; }
        /* Vars also live on <body> so themed content that portals out of the
           .themed-page subtree (FvSelect menus, dialogs, toasts) still inherits
           the organiser theme instead of falling back to dashboard defaults. */
        .themed-page, body {
          --fv-heading-font: ${getHeadingFontClass() || 'inherit'};
          --fv-body-font: ${getBodyFontFamily() || 'inherit'};
          --fv-heading-weight: ${getNumericHeaderWeight()};
          --fv-text: ${cardStyles.text};
          --fv-text-primary: ${cardStyles.text};
          --fv-text-secondary: ${cardStyles.muted};
          --fv-text-muted: ${cardStyles.muted};
          --fv-text-tertiary: ${cardStyles.muted};
          --fv-hover: ${getBgSecondary()};
          --fv-hover-strong: ${getBgSecondary()};
          --fv-border: ${cardStyles.border};
          --fv-border-strong: ${contentBoxStyle === 'black' ? '#4B5563' : contentBoxStyle === 'transparent' ? colors.text + '44' : '#D1D5DB'};
          --ticket-border: ${contentBoxStyle === 'black' ? '#6B7280' : contentBoxStyle === 'transparent' ? colors.text + '55' : '#D1D5DB'};
          --fv-border-hover: ${cardStyles.border};
          /* The page's own ground, as opposed to --fv-bg (the content box,
             which is literally transparent for the transparent box style).
             Anything pinned over the page (a fixed action bar) needs this to
             sit on, and needs it to follow the organiser's theme rather than
             assuming dark. */
          --fv-page-bg: ${colors.primary};
          --fv-bg: ${cardStyles.bg};
          --fv-bg-secondary: ${getBgSecondary()};
          --fv-bg-input: ${cardStyles.bg};
          --fv-bg-card: ${cardStyles.bg};
          --fv-bg-raised: ${
            contentBoxStyle === 'transparent'
              ? (isDarkColor(colors.text) ? '#ffffff' : `color-mix(in srgb, ${colors.primary}, #ffffff 8%)`)
              : (cardStyles.bg === '#FFFFFF' ? '#f3f4f6' : cardStyles.bg)
          };
          --fv-bg-overlay: ${
            contentBoxStyle === 'black'
              ? '#1a1917'
              : contentBoxStyle === 'transparent'
                ? (isDarkColor(colors.text) ? '#ffffff' : `color-mix(in srgb, ${colors.primary}, #ffffff 12%)`)
                : '#ffffff'
          };
          --fv-accent: ${colors.secondary};
          --fv-accent-border: ${colors.secondary}66;
          --fv-accent-light: ${colors.secondary}14;
          --fv-accent-bg: ${colors.secondary}1F;
          --fv-accent-text: ${colors.buttonText || 'white'};
          --fv-theme-text: ${colors.text};
          --fv-theme-text-muted: ${colors.textMuted || colors.text + '99'};
        }
        .themed-page h1,
        .themed-page h2,
        .themed-page h3 {
          ${getHeadingFontClass() ? `font-family: ${getHeadingFontClass()} !important;` : ''}
          ${getHeadingWeight().replace(';', ' !important;')}
          color: ${colors.text};
        }
        .themed-page .card-bg h1 { color: ${cardStyles.heading}; }
        ${fontStyle === 'custom' && customFonts?.body ? `.themed-page { font-family: ${getBodyFontFamily()}; }` : ''}
        .themed-page .text-muted { color: ${colors.textMuted} !important; }
        .themed-page .btn-primary {
          background-color: ${colors.secondary};
          border-color: ${colors.secondary};
          color: ${colors.buttonText || 'white'};
          border-radius: ${borderRadiusValue};
          display: flex; align-items: center; justify-content: center; text-align: center;
          transition: filter 0.1s ease, transform 0.1s ease, opacity 0.15s ease;
        }
        .themed-page .btn-primary.rounded-full { border-radius: 9999px; }
        .themed-page .btn-primary:hover { filter: brightness(0.9); }
        .themed-page .btn-primary:active { filter: brightness(0.75); transform: scale(0.98); }
        .themed-page .btn-primary.is-navigating { pointer-events: none; opacity: 0.7; filter: brightness(0.8); }
        /* fv-card hover uses a hardcoded teal shadow + un-themed accent border in
           globals.css; re-point it at the organiser accent on themed pages. */
        .themed-page a.fv-card:hover, .themed-page button.fv-card:hover {
          border-color: ${colors.secondary} !important;
          box-shadow: 0 2px 16px ${colors.secondary}22 !important;
        }
        .themed-page .border-themed { border-color: ${colors.secondary} !important; }
        .themed-page .bg-themed-secondary { background-color: ${colors.secondary}; }
        .themed-page .text-themed-secondary { color: ${colors.secondary}; }
        .themed-page input[type="radio"]:checked { accent-color: ${colors.secondary}; }
        .themed-page input[type="checkbox"] { accent-color: ${colors.secondary}; }
        .themed-page .card-bg {
          background-color: ${cardStyles.bg}; color: ${cardStyles.text};
          border-radius: ${borderRadiusValue}; border-color: ${cardStyles.border};
        }
        .themed-page .card-bg h2, .themed-page .card-bg h3 { color: ${cardStyles.heading}; }
        .themed-page .card-bg .text-muted { color: ${cardStyles.muted} !important; }
        .themed-page .card-bg.border, .themed-page .border.card-bg { border: 1px solid ${cardStyles.border}; }
        .themed-page .card-bg input[type="text"],
        .themed-page .card-bg input[type="email"],
        .themed-page .card-bg input[type="number"],
        .themed-page .card-bg input[type="date"],
        .themed-page .card-bg input[type="tel"],
        .themed-page .card-bg textarea,
        .themed-page .card-bg select {
          border-color: ${cardStyles.border}; color: ${cardStyles.text}; background: transparent;
        }
        .themed-page .card-bg input::placeholder,
        .themed-page .card-bg textarea::placeholder { color: ${cardStyles.muted}; }
        .themed-page .rounded-md, .themed-page .rounded-lg, .themed-page .rounded { border-radius: ${borderRadiusValue}; }
        .themed-page header.border-b { border-bottom: 1px solid ${colors.text}20; }
        .themed-page footer.border-t { border-top: 1px solid ${colors.text}20; }
        .themed-page [data-slot="checkbox"][data-state="checked"] {
          background-color: var(--fv-accent) !important; border-color: var(--fv-accent) !important;
        }
        .themed-page [data-slot="button"][data-variant="outline"] {
          background: transparent; color: var(--fv-text-primary); border-color: var(--fv-border);
        }
        .themed-page [data-slot="button"][data-variant="outline"]:hover {
          background: var(--fv-accent-dim); color: var(--fv-text-primary);
        }
        .themed-page input:focus,
        .themed-page select:focus,
        .themed-page textarea:focus {
          outline: none;
          border-color: var(--fv-accent) !important;
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--fv-accent) 25%, transparent);
        }
      ` }} />

      <div className="themed-page" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <header className="border-b" style={{ borderColor: 'var(--fv-border)' }}>
          <div className="mx-auto max-w-7xl px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                {showLogo && displayLogo && (
                  <img
                    src={displayLogo}
                    alt={displayName || 'Organizer'}
                    className="h-8 md:h-10 w-auto max-w-[40vw] md:max-w-none object-contain shrink-0"
                  />
                )}
                {showName && displayName && (
                  <span
                    className={`text-lg md:text-xl truncate ${showLogo && displayLogo ? 'hidden md:inline' : ''}`}
                    style={{
                      fontWeight: getNumericHeaderWeight(),
                      color: 'var(--fv-theme-text)',
                      ...(uploadedFonts?.brand
                        ? { fontFamily: `'${uploadedFonts.brand.name}', sans-serif` }
                        : brandNameFont
                        ? { fontFamily: `'${brandNameFont}', sans-serif` }
                        : getHeadingFontClass()
                        ? { fontFamily: getHeadingFontClass() }
                        : {}),
                    }}
                  >
                    {displayName}
                  </span>
                )}
              </div>
              {showPoweredBy && (
                <span className="shrink-0 whitespace-nowrap text-xs md:text-sm" style={{ color: fitventBrandingColor }}>
                  <PoweredByFitvent dark={isDarkColor(colors.primary)} />
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main>{children}</main>

        {/* Footer */}
        {showPoweredBy && (
          <footer className={`mt-12 border-t py-8 ${hasMobileTicketBar ? 'pb-32' : 'pb-8'} lg:pb-8`} style={{ borderColor: 'var(--fv-border)' }}>
            <div className="mx-auto max-w-7xl px-4 text-center text-sm" style={{ color: fitventBrandingColor }}>
              <PoweredByFitvent dark={isDarkColor(colors.primary)} />
              <p className="mt-1 text-xs opacity-60">One platform for every fitness event</p>
            </div>
          </footer>
        )}
      </div>
    </div>
    </>
  );
}

/**
 * "Powered by fitvent" with the mark, the same in the header and the footer.
 * The mark takes the brand pack's pair for the ground: cyan and bone on a
 * dark page, teal and ink on a light one.
 */
function PoweredByFitvent({ dark }: { dark: boolean }) {
  return (
    // Our lockup, in our own face: it is not part of the organiser's page, so
    // it must not take the heading or body font they picked (an imported
    // icon font once turned it into a serif fallback).
    <a href="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{ fontFamily: 'var(--fvd-font-body)' }}>
      <svg width="18" height="18" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <polygon points="8,14 92,14 92,28 22,28" fill={dark ? '#2ADFDF' : '#006D6D'} />
        <polygon points="34,44 92,44 92,58 48,58" fill={dark ? '#F4FAF8' : '#10201D'} />
        <polygon points="68,74 92,74 92,88 78,88" fill={dark ? '#F4FAF8' : '#10201D'} />
      </svg>
      {/* Beside the mark it is the logo, and the logo is always lowercase,
          set in the wordmark face (brand pack). */}
      <span>
        Powered by{' '}
        <span style={{ fontFamily: 'var(--fvd-font-wordmark)', fontWeight: 700, letterSpacing: '-0.043em' }}>fitvent</span>
      </span>
    </a>
  );
}
